(() => {
  'use strict';
  // One lightweight adapter for existing popup scroll locks. Observe only the
  // two page roots, never form fields or the catalog DOM. CSS reserves the
  // gutter before opening; this guard removes redundant legacy compensation.
  if (!window.GEMSPageScrollGuard) {
    const initializeScrollGuard = () => {
      if (window.GEMSPageScrollGuard || !document.body) return;
      const root = document.documentElement;
      const body = document.body;
      let locked = false;
      let padding = getComputedStyle(body).paddingRight;
      const sync = () => {
        const bodyStyle = getComputedStyle(body);
        const rootStyle = getComputedStyle(root);
        const fixed = bodyStyle.position === 'fixed';
        const nextLocked = fixed || /hidden|clip/.test(bodyStyle.overflowY) ||
          /hidden|clip/.test(rootStyle.overflowY) || /hidden|clip/.test(root.style.overflowY);
        if (nextLocked && !locked) {
          root.style.setProperty('--gems-page-padding-right', padding);
          root.setAttribute('data-gems-scroll-locked', '');
        } else if (!nextLocked && locked) {
          root.removeAttribute('data-gems-scroll-locked');
          root.style.removeProperty('--gems-page-padding-right');
        }
        root.toggleAttribute('data-gems-fixed-body-lock', fixed);
        if (!nextLocked) padding = getComputedStyle(body).paddingRight;
        const changed = locked !== nextLocked;
        locked = nextLocked;
        document.querySelectorAll('gems-header').forEach(header => {
          // External forms may also fix the body. Keep the mobile reservation
          // and pin together before the next paint, including lock handoffs.
          header.syncMobileLockState?.();
          if (changed) header.scheduleMeasure?.();
        });
      };
      const observer = new MutationObserver(sync);
      observer.observe(body, { attributes: true, attributeFilter: ['class', 'style'] });
      observer.observe(root, { attributes: true, attributeFilter: ['class', 'style'] });
      window.GEMSPageScrollGuard = { sync, get locked() { return locked; } };
      sync();
    };
    if (document.body) initializeScrollGuard();
    else document.addEventListener('DOMContentLoaded', initializeScrollGuard, { once: true });
  }
  const ELEMENT_NAME = 'gems-header';
  const MOBILE_QUERY = '(max-width: 1023px)';
  const FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';
  if (customElements.get(ELEMENT_NAME)) return;
  class GemsHeader extends HTMLElement {
    connectedCallback() {
      if (this._connected) return;
      this._connected = true;
      this.setAttribute('data-gems-search-layout', 'content-sheet-20260924');
      this.lifecycle = new AbortController();
      this.drawerHideTimer = 0;
      this.drawerFrame = 0;
      this.lockReasons = new Set();
      this.lockedScrollY = 0;
      this.previousBodyStyles = null;
      this.measureFrame = 0;
      this.scrollFrame = 0;
      this.overlayFrame = 0;
      this.cartFrame = 0;
      this.cartHideTimer = 0;
      this.header = this.querySelector('[data-gems-header]');
      this.announcement = this.querySelector('[data-gems-announcement]');
      this.headerSpacer = this.querySelector('[data-gems-header-spacer]');
      this.headerAnchor = this.querySelector('[data-gems-header-anchor]');
      this.desktopMain = this.querySelector('[data-gems-desktop-main]');
      this.utilityRow = this.querySelector('.gems-header__top');
      this.drawer = this.querySelector('[data-gems-drawer]');
      this.drawerPanel = this.querySelector('.gems-drawer__panel');
      this.menuToggles = [...this.querySelectorAll('[data-gems-menu-toggle]')];
      this.cartPopover = this.querySelector('[data-gems-cart-popover]');
      this.cartToggles = [...this.querySelectorAll('[data-gems-cart-toggle]')];
      this.mobileSearch = this.querySelector('[data-gems-mobile-search]');
      this.searchBackdrop = this.querySelector('[data-gems-search-backdrop]');
      this.mobileSearchToggle = this.querySelector('[data-gems-mobile-search-toggle]');
      this.searchPortal = this.querySelector('[data-gems-search-portal]');
      this.desktopSearchSlot = this.querySelector('[data-gems-search-slot="desktop"]');
      this.mobileSearchSlot = this.querySelector('[data-gems-search-slot="mobile"]');
      this.searchHost = this.querySelector('[data-gems-search-host]');
      this.searchHelpToggles = [...this.querySelectorAll('[data-gems-search-help-toggle]')];
      this.searchHelpPanels = [...this.querySelectorAll('[data-gems-search-help]')];
      this.account = this.querySelector('[data-gems-account]');
      this.accountToggle = this.querySelector('[data-gems-account-toggle]');
      this.accountMenu = this.querySelector('[data-gems-account-menu]');
      this.sectionId = this.dataset.sectionId || '';
      this.stickyEnabled = this.header?.dataset.gemsStickyEnabled === 'true';
      this.mobile = window.matchMedia(MOBILE_QUERY).matches;
      this.layoutWidth = document.documentElement.clientWidth || window.innerWidth;
      this.desktopStickyStart = 0;
      this.desktopHeaderFixed = false;
      this.stickyOrigin = 0;
      this.stickyProgress = -1;
      this.mobileLockOffset = null;
      this.mobileLockHeight = null;
      this.setAttribute('data-gems-header-version', 'mobile-scroll-20261016');
      this.motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.drawerOpen = false;
      this.cartOpen = false;
      this.mobileSearchOpen = false;
      this.activeCartToggle = null;
      this.lastDrawerTrigger = null;
      this.restoreAnnouncementState();
      this.mountSearchPortal();
      this.prepareDrawerNavigation();
      this.prepareLocaleControls();
      this.observeAccountLinks();
      this.bindEvents();
      this.bindDesktopNavigation();
      this.bindAnnouncement();
      this.observeHeader();
      this.observeDrawerScroll();
      this.observeCartConfirmations();
      this.listen(document, 'DOMContentLoaded', () => this.observeCartConfirmations(), { once: true });
      // Set the initial geometry before an early pointer/focus event can open
      // an overlay. Later image/font/viewport changes still use the scheduler.
      this.measureLayout();
    }
    disconnectedCallback() {
      this._connected = false;
      this.lifecycle?.abort();
      this.resizeObserver?.disconnect();
      this.drawerResizeObserver?.disconnect();
      this.cartConfirmationObserver?.disconnect();
      this.accountLinksObserver?.disconnect();
      cancelAnimationFrame(this.drawerScrollFrame);
      cancelAnimationFrame(this.measureFrame);
      cancelAnimationFrame(this.scrollFrame);
      cancelAnimationFrame(this.overlayFrame);
      cancelAnimationFrame(this.cartFrame);
      clearTimeout(this.cartHideTimer);
      cancelAnimationFrame(this.drawerFrame);
      clearTimeout(this.drawerHideTimer);
      this.drawerScrollGuard?.abort();
      this.closeDrawer(false);
      cancelAnimationFrame(this.drawerScrollFrame);
      clearTimeout(this.drawerHideTimer);
      if (this.drawer) this.drawer.hidden = true;
      this.closeCart();
      this.closeMobileSearch();
      this.closeSearchHelp();
      this.closeAllMega();
      this.closeAccount();
      this.forceUnlockPage();
      this.cartConfirmations?.forEach((popup) => {
        popup.removeAttribute('data-gems-cart-confirmation');
        popup.removeAttribute('data-gems-cart-superseded');
        ['left', 'top', 'width', 'maxHeight'].forEach((key) => popup.style.removeProperty(`--gems-confirmation-${key}`));
      });
      this.resetStickyAppearance();
      this.header?.classList.remove('is-fixed', 'is-mobile-fixed');
      this.removeAttribute('data-gems-mobile-lock');
      this.style.removeProperty('--gems-mobile-lock-top');
      this.style.removeProperty('--gems-mobile-lock-height');
      this.headerSpacer?.classList.remove('is-active');
      this.headerSpacer?.style.removeProperty('height');
      if (this.utilityRow) this.utilityRow.inert = false;
    }
    listen(target, type, handler, options = {}) {
      if (!target) return;
      target.addEventListener(type, handler, { ...options, signal: this.lifecycle.signal });
    }
    repairAccountLink(link) {
      if (!link?.matches?.('a[href]')) return;
      try {
        const url = new URL(link.getAttribute('href'), window.location.href);
        if (url.origin !== window.location.origin) return;
        // Shopify supplies language and region in these routes' query strings.
        // A storefront locale prefix makes the authentication endpoint a 404.
        const pathname = url.pathname.replace(
          /^\/[a-z]{2}(?:-[a-z]{2})?(?=\/customer_authentication(?:\/|$))/i,
          ''
        );
        if (pathname === url.pathname) return;
        link.setAttribute('href', pathname + url.search + url.hash);
      } catch (_) {
        // Leave malformed or non-URL links to their existing handlers.
      }
    }
    observeAccountLinks() {
      const repairTree = (node) => {
        this.repairAccountLink(node);
        node.querySelectorAll?.('a[href]').forEach((link) => this.repairAccountLink(link));
      };
      repairTree(this);
      this.accountLinksObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes') this.repairAccountLink(mutation.target);
          else mutation.addedNodes.forEach(repairTree);
        });
      });
      // Keep actual hrefs correct for copied links, new tabs, and delayed
      // locale rewrites. Observe only this header and only href attributes.
      this.accountLinksObserver.observe(this, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ['href']
      });
      const repairInteraction = (event) => this.repairAccountLink(event.target.closest?.('a[href]'));
      ['pointerdown', 'click', 'auxclick', 'contextmenu', 'focusin'].forEach((type) => {
        this.listen(this, type, repairInteraction, { capture: true });
      });
    }
    bindEvents() {
      this.listen(this.accountToggle, 'click', () => {
        this.accountMenu?.hidden ? this.openAccount() : this.closeAccount();
      });
      this.listen(this.accountToggle, 'keydown', (event) => {
        if (event.key !== 'ArrowDown') return;
        event.preventDefault();
        this.openAccount();
        this.accountMenu?.querySelector('a')?.focus({ preventScroll:true });
      });
      this.listen(this.account, 'focusout', (event) => {
        if (!this.account.contains(event.relatedTarget)) this.closeAccount();
      });
      this.menuToggles.forEach((button) => {
        this.listen(button, 'click', (event) => {
          event.preventDefault();
          this.drawerOpen ? this.closeDrawer() : this.openDrawer(button);
        });
      });
      this.querySelectorAll('[data-gems-drawer-close]').forEach((button) => {
        this.listen(button, 'click', (event) => {
          event.preventDefault();
          this.closeDrawer();
        });
      });
      this.listen(this.drawer, 'click', (event) => {
        const button = event.target.closest('[data-gems-drawer-subtoggle]');
        if (!button || !this.drawer.contains(button)) return;
        event.preventDefault();
        event.stopPropagation();
        this.toggleDrawerSubmenu(button);
      }, { capture: true });
      this.cartToggles.forEach((button) => {
        this.listen(button, 'click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          if (this.cartOpen && this.activeCartToggle === button) this.closeCart();
          else this.openCart(button);
        });
      });
      this.listen(this.cartPopover, 'transitionend', (event) => {
        if (this.cartOpen || !this.cartPopover) return;
        const surface = this.cartPopover.querySelector('.gems-cart-popover__surface');
        const expectedTarget = this.mobile ? surface : this.cartPopover;
        if (event.target !== expectedTarget || event.propertyName !== 'transform') return;
        clearTimeout(this.cartHideTimer);
        this.cartPopover.hidden = true;
      });
      if (this.mobileSearchToggle) {
        this.listen(this.mobileSearchToggle, 'click', (event) => {
          event.preventDefault();
          this.mobileSearchOpen ? this.closeMobileSearch() : this.openMobileSearch();
        });
      }
      this.listen(this.searchBackdrop, 'click', (event) => {
        event.preventDefault();
        this.closeMobileSearch();
        this.mobileSearchToggle?.focus({ preventScroll: true });
      });
      // Navigation closes the complete mobile surface, not just its results.
      this.listen(this.searchHost, 'gems:search-navigate', () => {
        if (this.mobileSearchOpen) this.closeMobileSearch();
      });
      // Only a genuinely new notification may clear a previous handoff's mask.
      this.listen(document, 'gems:cart-confirmation-show', (event) => {
        const popup = event.detail?.wrapper;
        if (!this.cartConfirmations?.includes(popup)) return;
        popup.toggleAttribute('data-gems-cart-superseded', !this.mobile && this.cartOpen);
        this.syncCartConfirmations();
      });
      this.searchHelpToggles.forEach((button) => {
        this.listen(button, 'click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.toggleSearchHelp(button);
        });
      });
      this.querySelectorAll('[data-gems-search-help-close]').forEach((button) => {
        this.listen(button, 'click', (event) => {
          event.preventDefault();
          const trigger = this.searchHelpToggles.find((toggle) => toggle.getAttribute('aria-expanded') === 'true');
          this.closeSearchHelp(null, true);
          trigger?.focus({ preventScroll: true });
        });
      });
      // Search owns the next interaction, including clicks stopped by nested controls.
      const dismissForSearch = () => {
        this.closeAccount();
        if (this.drawerOpen) this.closeDrawer(false);
        if (this.cartOpen) this.closeCart();
        this.closeAllMega();
        this.closeLocaleControls();
      };
      this.listen(this.searchHost, 'pointerdown', dismissForSearch, { capture: true });
      this.listen(this.searchHost, 'click', dismissForSearch, { capture: true });
      this.listen(this.searchHost, 'focus', (event) => {
        dismissForSearch();
        if (event.target.matches('[data-gems-search-input]')) this.closeSearchHelp();
      }, { capture: true });
      this.listen(document, 'click', (event) => this.handleDocumentClick(event));
      this.listen(document, 'keydown', (event) => this.handleKeydown(event));
      this.listen(window, 'resize', () => this.handleResize(), { passive: true });
      this.listen(window, 'orientationchange', () => this.handleResize(true), { passive: true });
      this.listen(window, 'scroll', () => this.scheduleScrollUpdate(), { passive: true });
      this.listen(window, 'pageshow', () => this.scheduleMeasure());
      this.listen(window, 'load', () => this.scheduleMeasure(), { once: true });
      this.listen(this, 'load', (event) => {
        if (event.target.matches?.('.gems-logo__image')) this.scheduleMeasure();
      }, { capture: true });
      this.listen(this.motionPreference, 'change', () => {
        this.stickyProgress = -1;
        this.scheduleScrollUpdate();
      });
      if (window.visualViewport) {
        this.listen(window.visualViewport, 'resize', () => this.scheduleOverlayPosition(), { passive: true });
        this.listen(window.visualViewport, 'scroll', () => this.scheduleOverlayPosition(), { passive: true });
      }
      if (document.fonts?.ready) {
        document.fonts.ready.then(() => {
          if (this.isConnected) this.scheduleMeasure();
        }).catch(() => {});
      }
    }
    bindDesktopNavigation() {
      this.querySelectorAll('[data-gems-nav-item]').forEach((item) => {
        const toggle = item.querySelector('[data-gems-nav-toggle]');
        const panel = item.querySelector('[data-gems-mega]');
        if (!toggle || !panel) return;
        this.listen(toggle, 'click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          const wasOpen = item.classList.contains('is-open');
          this.closeAllMega();
          if (!wasOpen) this.openMega(item);
        });
        // Disclosure navigation opens only through the button, never pointer travel.
        this.listen(item, 'focusout', (event) => {
          if (event.relatedTarget && item.contains(event.relatedTarget)) return;
          this.closeMega(item);
        });
      });
    }
    bindAnnouncement() {
      const closeButton = this.querySelector('[data-gems-announcement-close]');
      if (!this.announcement || !closeButton) return;
      this.listen(closeButton, 'click', () => {
        this.announcement.hidden = true;
        try { sessionStorage.setItem(`gemsAnnouncement:${this.sectionId}`, 'closed'); } catch (_) {}
        this.scheduleMeasure();
      });
    }
    restoreAnnouncementState() {
      if (!this.announcement) return;
      try {
        if (sessionStorage.getItem(`gemsAnnouncement:${this.sectionId}`) === 'closed') this.announcement.hidden = true;
      } catch (_) {}
    }
    observeHeader() {
      if (!this.header || typeof ResizeObserver === 'undefined') return;
      // Visual transforms do not resize these boxes. Geometry can be refreshed
      // while pinned without ever flashing the expanded header back into view.
      this.resizeObserver = new ResizeObserver(() => this.scheduleMeasure());
      this.resizeObserver.observe(this.header);
      if (this.desktopMain) this.resizeObserver.observe(this.desktopMain);
      if (this.utilityRow) this.resizeObserver.observe(this.utilityRow);
      if (this.announcement) this.resizeObserver.observe(this.announcement);
    }
    observeDrawerScroll() {
      this.drawerNav = this.drawer?.querySelector('.gems-drawer__nav');
      this.drawerViewport = this.drawer?.querySelector('[data-gems-drawer-viewport]');
      this.drawerScrollFrame = 0;
      if (!this.drawerNav || !this.drawerViewport) return;
      this.listen(this.drawerNav, 'scroll', () => this.scheduleDrawerScroll(), { passive: true });
      this.drawerResizeObserver = new ResizeObserver(() => this.scheduleDrawerScroll());
      this.drawerResizeObserver.observe(this.drawerNav);
      const content = this.drawerNav.firstElementChild;
      if (content) this.drawerResizeObserver.observe(content);
    }
    scheduleDrawerScroll() {
      if (this.drawerScrollFrame) return;
      this.drawerScrollFrame = requestAnimationFrame(() => {
        this.drawerScrollFrame = 0;
        const list = this.drawerNav;
        const viewport = this.drawerViewport;
        if (!list || !viewport) return;
        const maxScroll = Math.max(0, list.scrollHeight - list.clientHeight);
        const scrollable = this.drawerOpen && list.clientHeight > 0 && maxScroll > 2;
        viewport.classList.toggle('is-scrollable', scrollable);
        viewport.classList.toggle('is-at-top', !scrollable || list.scrollTop <= 2);
        viewport.classList.toggle('is-at-bottom', !scrollable || list.scrollTop >= maxScroll - 2);
      });
    }
    prepareLocaleControls() {
      this.localeControls = ['language', 'currency'].map(kind => {
        const header = this.querySelector(`[data-gems-locale-slot="${kind}"][data-gems-locale-location="header"]`);
        const drawer = this.querySelector(`[data-gems-locale-slot="${kind}"][data-gems-locale-location="drawer"]`);
        const group = header?.querySelector('.lang_currency-dropdown') || drawer?.querySelector('.lang_currency-dropdown');
        const label = group?.querySelector('.dropdown-label');
        const menu = group?.querySelector('.dropdown-menu');
        if (!group || !label || !menu) return null;
        group.setAttribute('data-gems-locale', kind);
        label.setAttribute('role', 'button');
        label.setAttribute('tabindex', '0');
        label.setAttribute('aria-expanded', 'false');
        if (!menu.id) menu.id = `GemsLocale-${this.sectionId}-${kind}`;
        label.setAttribute('aria-controls', menu.id);
        menu.hidden = true;
        group.classList.remove('is-open');
        return { header, drawer, group, label, menu };
      }).filter(Boolean);
      this.mountLocaleControls();
      this.listen(this, 'click', event => {
        const label = event.target.closest('.dropdown-label');
        const record = this.localeControls.find(item => item.label === label);
        if (!record) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        const open = record.menu.hidden;
        this.closeLocaleControls();
        if (open) {
          this.closeAccount();
          record.menu.hidden = false;
          record.label.setAttribute('aria-expanded', 'true');
          record.group.classList.add('is-open');
          this.positionLocaleControls();
        }
      }, { capture: true });
      this.listen(this, 'keydown', event => {
        if (!this.localeControls.some(item => item.label === event.target)) return;
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        event.stopPropagation();
        event.target.click();
      });
    }
    mountLocaleControls() {
      this.localeControls?.forEach(record => {
        const slot = (this.mobile || this.drawerOpen) ? record.drawer : record.header || record.drawer;
        if (slot && record.group.parentElement !== slot) slot.appendChild(record.group);
      });
    }
    closeLocaleControls(restoreFocus = false) {
      this.localeControls?.forEach(record => {
        const wasOpen = !record.menu.hidden;
        record.menu.hidden = true;
        record.label.setAttribute('aria-expanded', 'false');
        record.group.classList.remove('is-open');
        if (wasOpen && restoreFocus) record.label.focus({ preventScroll: true });
      });
    }
    positionLocaleControls() {
      if (!this.drawerOpen || !this.drawerPanel) return;
      const top = this.drawerPanel.getBoundingClientRect().top;
      this.localeControls?.forEach(record => {
        if (record.menu.hidden) return;
        record.menu.style.setProperty('--gems-locale-menu-height', `${Math.max(44, record.label.getBoundingClientRect().top - top - 8)}px`);
      });
    }
    prepareDrawerNavigation() {
      // Upgrade the previous split title/arrow markup when an older section is rendered.
      this.drawer?.querySelectorAll('.gems-drawer-item__row').forEach((row) => {
        const button = row.querySelector('[data-gems-drawer-subtoggle]');
        const link = row.querySelector(':scope > a');
        const item = row.closest('.gems-drawer-item');
        const submenu = item?.querySelector(':scope > [data-gems-drawer-submenu]');
        if (!button || !link || !submenu) return;
        const destination = submenu.querySelector('.gems-drawer-submenu__all');
        if (!destination) return;
        destination.setAttribute('href', link.getAttribute('href'));
        const label = document.createElement('span');
        label.className = 'gems-drawer-toggle__label';
        label.append(...link.childNodes);
        button.prepend(label);
        button.removeAttribute('aria-label');
        link.remove();
        item.setAttribute('data-gems-drawer-item', '');
      });
    }
    toggleDrawerSubmenu(button) {
      const item = button.closest('[data-gems-drawer-item], .gems-drawer-item, .gems-drawer-child');
      const submenu = item && [...item.children].find(node =>
        node.hasAttribute('data-gems-drawer-submenu') && node.id === button.getAttribute('aria-controls'));
      if (!item || !submenu) return;
      const open = button.getAttribute('aria-expanded') !== 'true';
      button.setAttribute('aria-expanded', String(open));
      item.classList.toggle('is-open', open);
      submenu.hidden = !open;
      this.scheduleDrawerScroll();
    }
    openDrawer(trigger) {
      if (!this.drawer || !this.drawerPanel) return;
      this.closeAccount();
      this.closeCart();
      this.closeMobileSearch();
      this.closeSearchHelp();
      this.closeAllMega();
      clearTimeout(this.drawerHideTimer);
      cancelAnimationFrame(this.drawerFrame);
      this.drawer.inert = false;
      this.drawerOpen = true;
      this.closeLocaleControls();
      this.mountLocaleControls();
      this.lastDrawerTrigger = trigger || document.activeElement;
      this.menuToggles.forEach((button) => {
        button.classList.add('is-active');
        button.setAttribute('aria-expanded', 'true');
      });
      if (this.mobile) this.lockPage('drawer');
      else {
        this.drawerScrollGuard?.abort();
        this.drawerScrollGuard = new AbortController();
        for (const type of ['wheel', 'touchmove']) {
          document.addEventListener(type, (event) => this.guardDesktopOverlayScroll(event), {
            capture: true, passive: false, signal: this.drawerScrollGuard.signal
          });
        }
      }
      this.syncMobileLockState();
      this.positionDrawer();
      this.drawer.hidden = false;
      this.drawerFrame = requestAnimationFrame(() => {
        this.drawerFrame = 0;
        if (!this.drawerOpen) return;
        this.positionDrawer();
        this.drawer.classList.add('is-open');
        this.scheduleDrawerScroll();
        this.drawerPanel.focus({ preventScroll: true });
      });
    }
    closeDrawer(restoreFocus = true) {
      if (!this.drawer) return;
      const wasOpen = this.drawerOpen;
      cancelAnimationFrame(this.drawerFrame);
      clearTimeout(this.drawerHideTimer);
      this.drawerScrollGuard?.abort();
      this.drawer.inert = true;
      this.drawerOpen = false;
      this.closeLocaleControls();
      this.mountLocaleControls();
      this.scheduleDrawerScroll();
      this.drawer.classList.remove('is-open');
      this.menuToggles.forEach((button) => {
        button.classList.remove('is-active');
        button.setAttribute('aria-expanded', 'false');
      });
      this.unlockPage('drawer');
      if (wasOpen) {
        this.drawerHideTimer = window.setTimeout(() => {
          if (!this.drawerOpen) this.drawer.hidden = true;
        }, window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 240);
      } else {
        this.drawer.hidden = true;
      }
      if (restoreFocus && wasOpen && this.lastDrawerTrigger && document.contains(this.lastDrawerTrigger)) {
        this.lastDrawerTrigger.focus({ preventScroll: true });
      }
    }
    openCart(trigger) {
      if (!this.cartPopover || !trigger) return;
      this.closeAccount();
      this.closeDrawer(false);
      this.closeMobileSearch();
      this.closeSearchHelp();
      this.closeAllMega();
      clearTimeout(this.cartHideTimer);
      cancelAnimationFrame(this.cartFrame);
      this.cartOpen = true;
      this.activeCartToggle = trigger;
      this.syncCartConfirmations();
      this.cartPopover.inert = false;
      this.cartPopover.hidden = false;
      this.cartPopover.classList.remove('is-open');
      this.cartToggles.forEach((button) => {
        const active = button === trigger;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-expanded', String(active));
      });
      this.positionCart();
      if (this.mobile) this.lockPage('cart');
      this.cartFrame = requestAnimationFrame(() => {
        if (!this.cartOpen || !this.cartPopover) return;
        this.cartPopover.classList.add('is-open');
      });
      // The existing dropdown-cart.liquid remains authoritative for cart data,
      // remove actions, savings, free-shipping progress, and scroll cues. Opening
      // the panel performs no network request and no HTML replacement, preventing
      // the flash that occurred when the previous header refreshed on every tap.
    }
    closeCart() {
      if (!this.cartPopover) return;
      const wasOpen = this.cartOpen || this.cartPopover.classList.contains('is-open');
      cancelAnimationFrame(this.cartFrame);
      clearTimeout(this.cartHideTimer);
      this.cartOpen = false;
      this.activeCartToggle = null;
      this.syncCartConfirmations();
      this.cartPopover.inert = true;
      this.cartPopover.classList.remove('is-open');
      this.cartToggles.forEach((button) => {
        button.classList.remove('is-active');
        button.setAttribute('aria-expanded', 'false');
      });
      this.unlockPage('cart');
      if (!wasOpen || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.cartPopover.hidden = true;
        return;
      }
      this.cartHideTimer = window.setTimeout(() => {
        if (!this.cartOpen && this.cartPopover) this.cartPopover.hidden = true;
      }, 320);
    }
    openMobileSearch() {
      if (!this.mobile || !this.mobileSearch || !this.mobileSearchToggle) return;
      this.closeAccount();
      this.closeCart();
      this.closeDrawer(false);
      this.closeSearchHelp();
      this.closeAllMega();
      this.mountSearchPortal();
      this.mobileSearchOpen = true;
      this.setAttribute('data-gems-mobile-search-open', 'true');
      this.lockPage('search');
      this.syncMobileLockState();
      this.positionMobileSearch();
      this.mobileSearch.hidden = false;
      if (this.searchBackdrop) this.searchBackdrop.hidden = false;
      this.mobileSearch.classList.add('is-open');
      this.mobileSearchToggle.classList.add('is-active', 'is-open');
      this.mobileSearchToggle.setAttribute('aria-expanded', 'true');
      this.syncSearchHelpGeometry();
      this.openSmartSearch();
      const input = this.searchHost?.querySelector('[data-gems-search-input]');
      if (input) {
        const placeholder = input.dataset.gemsPlaceholder || input.getAttribute('placeholder') || '';
        if (placeholder) {
          input.dataset.gemsPlaceholder = placeholder;
          if (!input.value) input.setAttribute('placeholder', placeholder);
        }
      }
      // Opening the magnifier reveals Smart Search but deliberately does not
      // focus the field. This preserves the placeholder and prevents iOS from
      // opening/zooming the keyboard until the customer taps the input itself.
    }
    closeMobileSearch() {
      if (!this.mobileSearch || !this.mobileSearchToggle) return;
      this.mobileSearchOpen = false;
      this.removeAttribute('data-gems-mobile-search-open');
      if (this.searchBackdrop) this.searchBackdrop.hidden = true;
      this.closeSearchHelp();
      this.mobileSearch.classList.remove('is-open');
      this.mobileSearch.hidden = true;
      this.mobileSearchToggle.classList.remove('is-active', 'is-open');
      this.mobileSearchToggle.setAttribute('aria-expanded', 'false');
      this.closeSmartSearch();
      this.unlockPage('search');
      this.mobileSearch.style.removeProperty('--gems-mobile-search-height');
    }
    mountSearchPortal() {
      if (!this.searchPortal) return;
      const slot = this.mobile ? this.mobileSearchSlot : this.desktopSearchSlot;
      if (slot && this.searchPortal.parentElement !== slot) slot.appendChild(this.searchPortal);
    }
    toggleSearchHelp(button) {
      const id = button?.getAttribute('aria-controls');
      const panel = id ? this.querySelector(`#${CSS.escape(id)}`) : null;
      if (!panel) return;
      const opening = panel.hidden;
      if (!opening) {
        this.closeSearchHelp(null, true);
        return;
      }
      this.closeSearchHelp(panel);
      this.closeAccount();
      this.closeCart();
      this.closeDrawer(false);
      this.closeAllMega();
      this.closeSmartSearch();
      panel.hidden = false;
      this.positionSearchHelp();
      button.classList.add('is-active');
      button.setAttribute('aria-expanded', 'true');
    }
    closeSearchHelp(except = null, restoreSearch = false) {
      const wasOpen = this.searchHelpPanels.some((panel) => !panel.hidden && panel !== except);
      this.searchHelpPanels.forEach((panel) => {
        if (panel === except) return;
        panel.hidden = true;
        this.searchHelpToggles.forEach((button) => {
          if (button.getAttribute('aria-controls') === panel.id) {
            button.classList.remove('is-active');
            button.setAttribute('aria-expanded', 'false');
          }
        });
      });
      if (!except) {
        this.searchHelpToggles.forEach((button) => {
          button.classList.remove('is-active');
          button.setAttribute('aria-expanded', 'false');
        });
      }
      if (wasOpen && restoreSearch && this.mobile && this.mobileSearchOpen) this.openSmartSearch();
    }
    openSmartSearch() {
      this.searchHost?.querySelector('[data-gems-smart-search]')?.dispatchEvent(
        new CustomEvent('gems:search-panel-request', { detail: { open: true } })
      );
    }
    closeSmartSearch() {
      this.searchHost?.querySelector('[data-gems-smart-search]')?.dispatchEvent(
        new CustomEvent('gems:search-panel-request', { detail: { open: false } })
      );
      const input = this.searchHost?.querySelector('[data-gems-search-input]');
      const panel = this.searchHost?.querySelector('[data-gems-search-panel]');
      input?.setAttribute('aria-expanded', 'false');
      try { input?.blur(); } catch (_) {}
      panel?.classList.remove('is-open');
      panel?.setAttribute('aria-hidden', 'true');
    }
    openAccount() {
      if (this.mobile || !this.accountMenu || !this.accountToggle) return;
      this.closeDrawer(false);
      this.closeCart();
      this.closeSearchHelp();
      this.closeSmartSearch();
      this.closeAllMega();
      this.closeLocaleControls();
      this.accountMenu.hidden = false;
      this.accountToggle.setAttribute('aria-expanded', 'true');
    }
    closeAccount(restoreFocus = false) {
      if (!this.accountMenu || !this.accountToggle) return;
      const wasOpen = !this.accountMenu.hidden;
      this.accountMenu.hidden = true;
      this.accountToggle.setAttribute('aria-expanded', 'false');
      if (wasOpen && restoreFocus) this.accountToggle.focus({ preventScroll:true });
    }
    openMega(item) {
      if (!item || this.mobile) return;
      this.closeAccount();
      this.closeCart();
      this.closeDrawer(false);
      this.closeSearchHelp();
      this.closeSmartSearch();
      this.closeAllMega(item);
      const toggle = item.querySelector('[data-gems-nav-toggle]');
      const panel = item.querySelector('[data-gems-mega]');
      if (!toggle || !panel) return;
      item.classList.add('is-open');
      toggle.setAttribute('aria-expanded', 'true');
      panel.hidden = false;
    }
    closeMega(item) {
      if (!item) return;
      item.classList.remove('is-open');
      item.querySelector('[data-gems-nav-toggle]')?.setAttribute('aria-expanded', 'false');
      const panel = item.querySelector('[data-gems-mega]');
      if (panel) panel.hidden = true;
    }
    closeAllMega(except = null) {
      this.querySelectorAll('[data-gems-nav-item].is-open').forEach((item) => {
        if (item !== except) this.closeMega(item);
      });
    }
    lockPage(reason) {
      if (!this.mobile || this.lockReasons.has(reason)) return;
      const preservedScrollY = this.getPageScrollY();
      this.lockReasons.add(reason);
      if (this.lockReasons.size !== 1) return;
      this.lockedScrollY = preservedScrollY;
      const body = document.body;
      this.previousBodyStyles = {
        position: body.style.position,
        top: body.style.top,
        left: body.style.left,
        right: body.style.right,
        width: body.style.width,
        overflow: body.style.overflow
      };
      document.documentElement.classList.add('gems-overlay-lock');
      body.classList.add('gems-overlay-lock');
      body.style.position = 'fixed';
      body.style.top = `-${this.lockedScrollY}px`;
      body.style.left = '0';
      body.style.right = '0';
      body.style.width = '100%';
      body.style.overflow = 'hidden';
      window.GEMSPageScrollGuard?.sync();
    }
    unlockPage(reason) {
      if (!this.lockReasons.has(reason)) return;
      this.lockReasons.delete(reason);
      if (this.lockReasons.size) return;
      this.restorePageLock();
    }
    forceUnlockPage() {
      this.lockReasons.clear();
      this.restorePageLock();
    }
    restorePageLock() {
      if (!this.previousBodyStyles) return;
      const body = document.body;
      const styles = this.previousBodyStyles;
      document.documentElement.classList.remove('gems-overlay-lock');
      body.classList.remove('gems-overlay-lock');
      body.style.position = styles.position;
      body.style.top = styles.top;
      body.style.left = styles.left;
      body.style.right = styles.right;
      body.style.width = styles.width;
      body.style.overflow = styles.overflow;
      this.previousBodyStyles = null;
      const y = this.lockedScrollY;
      // Restore before another overlay can acquire the lock. Smooth scrolling
      // here would capture the wrong position on rapid cart/menu switches.
      window.scrollTo({ top: y, left: window.scrollX, behavior: 'instant' });
      window.GEMSPageScrollGuard?.sync();
    }
    guardDesktopOverlayScroll(event) {
      if (this.mobile || !event.cancelable || !this.drawerOpen) return;
      if (event.target.closest?.('.gems-drawer__nav, .gems-drawer__locale-control .dropdown-menu')) return;
      event.preventDefault();
    }
    handleResize(force = false) {
      const width = document.documentElement.clientWidth || window.innerWidth;
      const widthChanged = width !== this.layoutWidth;
      this.layoutWidth = width;
      const mobile = window.matchMedia(MOBILE_QUERY).matches;
      if (mobile !== this.mobile) {
        this.closeAccount();
        this.closeCart();
        this.closeDrawer(false);
        this.closeMobileSearch();
        this.closeSearchHelp();
        this.closeAllMega();
        this.forceUnlockPage();
        this.mobile = mobile;
        this.mountSearchPortal();
        this.mountLocaleControls();
      }
      if (this.mobile && !widthChanged && !force) {
        this.scheduleOverlayPosition();
        return;
      }
      this.scheduleMeasure();
    }
    scheduleMeasure() {
      if (this.measureFrame) return;
      this.measureFrame = requestAnimationFrame(() => {
        this.measureFrame = 0;
        this.measureLayout();
      });
    }
    measureLayout() {
      if (!this.header || !this.headerSpacer) return;
      this.syncSearchHelpGeometry();
      if (this.mobile) {
        const owner = this.closest('.shopify-section') || this;
        const announcementHeight = this.announcement && !this.announcement.hidden
          ? this.announcement.getBoundingClientRect().height : 0;
        const value = `${announcementHeight}px`;
        if (owner.style.getPropertyValue('--gems-mobile-announcement-height') !== value) {
          owner.style.setProperty('--gems-mobile-announcement-height', value);
        }
        this.desktopHeaderFixed = false;
        this.resetStickyAppearance();
        if (this.utilityRow) this.utilityRow.inert = false;
        // CSS owns mobile sticky positioning from the very first paint. It
        // needs no scroll offset or spacer unless a popup fixes the body.
        this.header.classList.remove('is-fixed', 'is-mobile-fixed');
        this.header.style.removeProperty('--gems-mobile-fixed-offset');
        this.headerSpacer.classList.remove('is-active');
        this.headerSpacer.style.removeProperty('height');
        this.syncMobileLockState();
      } else {
        const anchor = this.headerAnchor || this.header;
        // Geometry must use the raw scroll coordinate: clamping Safari's
        // negative overscroll here would permanently inflate the origin.
        this.stickyOrigin = anchor.getBoundingClientRect().top + this.getPageScrollY(false);
        this.syncMobileLockState();
        this.header.classList.remove('is-mobile-fixed');
        this.header.style.removeProperty('--gems-mobile-fixed-offset');
        // The main row keeps its natural layout in both states. The utility row
        // remains measurable above the viewport when pinned; no class toggling
        // is needed to discover the expanded dimensions.
        this.desktopMainHeight = this.desktopMain?.getBoundingClientRect().height || 0;
        const utilityHeight = this.utilityRow?.getBoundingClientRect().height || 0;
        this.naturalHeight = utilityHeight + this.desktopMainHeight;
        this.headerSpacer.style.height = `${this.naturalHeight}px`;
        const configuredHeight = Number.parseFloat(getComputedStyle(this).getPropertyValue('--gems-sticky-height')) || 70;
        this.desktopCompactHeight = Math.min(this.desktopMainHeight, Math.max(52, configuredHeight));
        this.desktopCollapseDistance = Math.max(80, this.desktopMainHeight);
        // Pin exactly when the main black row reaches the viewport's top.
        // Service/navigation/parts rows stay in flow and never enter this sum.
        this.desktopStickyStart = this.stickyOrigin + utilityHeight;
        this.updateDesktopSticky(true);
      }
      this.positionOpenOverlays();
    }
    getPageScrollY(clamp = true) {
      // Header overlays and external form/modals can both fix the body. The
      // negative body offset preserves the real document position while the
      // browser reports scrollY=0. Never treat that lock as scrolling upward.
      if (this.lockReasons.size) return this.lockedScrollY;
      const bodyStyle = getComputedStyle(document.body);
      const bodyTop = Number.parseFloat(bodyStyle.top);
      if (bodyStyle.position === 'fixed' && Number.isFinite(bodyTop) && bodyTop <= 0) {
        return -bodyTop;
      }
      const y = window.scrollY ?? window.pageYOffset ?? 0;
      return clamp ? Math.max(0, y) : y;
    }
    hasOpenOverlays() {
      return this.drawerOpen || this.cartOpen || this.mobileSearchOpen ||
        this.searchHelpPanels?.some(panel => !panel.hidden) ||
        this.cartConfirmations?.some(popup => !popup.hidden &&
          !popup.classList.contains('cart-popup-wrapper--hidden') &&
          !popup.hasAttribute('data-gems-cart-superseded'));
    }
    scheduleScrollUpdate() {
      if (this.mobile && !this.hasOpenOverlays()) return;
      if (this.scrollFrame) return;
      this.scrollFrame = requestAnimationFrame(() => {
        this.scrollFrame = 0;
        if (!this.mobile) this.updateDesktopSticky();
        // Re-anchor open overlays in the same paint cycle as the sticky-state
        // change so the cart/help UI never trails the header by one frame.
        this.positionOpenOverlays();
      });
    }
    syncMobileLockState() {
      if (!this.header) return;
      const locked = this.mobile && this.stickyEnabled &&
        (window.GEMSPageScrollGuard?.locked || getComputedStyle(document.body).position === 'fixed');
      if (locked) {
        // Only a visible announcement may reserve space above the mobile bar.
        // An empty flow anchor can move during body locking or focus-driven
        // viewport changes; copying that offset would create a blank strip.
        const announcement = !this.announcement?.hidden && this.announcement?.getBoundingClientRect();
        const top = announcement ? Math.max(0, Math.min(announcement.height, announcement.bottom)) : 0;
        const height = this.header.querySelector('.gems-mobile-bar')?.getBoundingClientRect().height ||
          Number.parseFloat(getComputedStyle(this).getPropertyValue('--gems-mobile-height')) || 64;
        if (top !== this.mobileLockOffset) this.style.setProperty('--gems-mobile-lock-top', `${top}px`);
        if (height !== this.mobileLockHeight) this.style.setProperty('--gems-mobile-lock-height', `${height}px`);
        this.mobileLockOffset = top;
        this.mobileLockHeight = height;
      } else if (this.mobileLockOffset !== null || this.mobileLockHeight !== null) {
        this.style.removeProperty('--gems-mobile-lock-top');
        this.style.removeProperty('--gems-mobile-lock-height');
        this.mobileLockOffset = this.mobileLockHeight = null;
      }
      this.toggleAttribute('data-gems-mobile-lock', locked);
    }
    updateDesktopSticky(force = false) {
      if (this.mobile || !this.header || !this.headerSpacer) return;
      const y = this.getPageScrollY();
      const shouldFix = Boolean(this.stickyEnabled && this.desktopMainHeight > 0 && y >= this.desktopStickyStart);
      if (force || shouldFix !== this.desktopHeaderFixed) {
        const entering = shouldFix && !this.desktopHeaderFixed;
        this.desktopHeaderFixed = shouldFix;
        this.header.classList.toggle('is-fixed', shouldFix);
        this.headerSpacer.classList.toggle('is-active', shouldFix);
        if (this.utilityRow) this.utilityRow.inert = shouldFix;
        if (entering) {
          this.closeAllMega();
          this.closeSearchHelp();
        }
      }
      const progress = shouldFix ? Math.min(1, Math.max(0, (y - this.desktopStickyStart) / this.desktopCollapseDistance)) : 0;
      if (!force && progress === this.stickyProgress) return;
      this.stickyProgress = progress;
      // Smoothstep joins the normal and compact states with zero slope at
      // either end. It follows the scroll in reverse without queued animations.
      const eased = progress * progress * (3 - 2 * progress);
      const decoration = this.motionPreference.matches ? Number(shouldFix) : eased;
      const reduction = (this.desktopMainHeight - this.desktopCompactHeight) * eased;
      this.desktopVisualHeight = this.desktopMainHeight - reduction;
      this.header.style.setProperty('--gems-sticky-bar-scale', String(this.desktopMainHeight > 0 ? this.desktopVisualHeight / this.desktopMainHeight : 1));
      this.header.style.setProperty('--gems-sticky-content-y', `${-reduction / 2}px`);
      this.header.style.setProperty('--gems-sticky-logo-scale', String(1 - .26 * decoration));
      this.header.style.setProperty('--gems-sticky-menu-opacity', String(decoration));
      this.header.style.setProperty('--gems-sticky-menu-scale', String(.88 + .12 * decoration));
      this.header.style.setProperty('--gems-sticky-shadow-opacity', String(eased));
    }
    resetStickyAppearance() {
      this.stickyProgress = -1;
      ['bar-scale', 'content-y', 'logo-scale', 'menu-opacity', 'menu-scale', 'shadow-opacity'].forEach((name) => {
        this.header?.style.removeProperty(`--gems-sticky-${name}`);
      });
    }
    syncSearchHelpGeometry() {
      if (!this.searchHost) return;
      const submit = this.searchHost.querySelector('.btn-search, [type="submit"]');
      if (!submit || submit.offsetParent === null) return;
      const width = Math.max(36, Math.ceil(submit.getBoundingClientRect().width));
      this.searchHost.style.setProperty('--gems-search-submit-width', `${width}px`);
    }
    positionSearchHelp() {
      if (!this.mobile || !this.mobileSearch || this.mobileSearch.hidden) return;
      const panel = this.searchHelpPanels.find((item) => !item.hidden);
      if (!panel) return;
      const viewport = window.visualViewport;
      const form = this.searchHost?.querySelector('[data-gems-search-form]');
      const top = Math.max(viewport?.offsetTop || 0, (form?.getBoundingClientRect().bottom || this.getVisibleHeaderBottom()) + 12);
      panel.style.setProperty('--gems-help-top', `${Math.round(top)}px`);
      panel.style.setProperty('--gems-help-left', `${Math.round(viewport?.offsetLeft || 0)}px`);
      panel.style.setProperty('--gems-help-width', `${Math.round(viewport?.width || document.documentElement.clientWidth)}px`);
      const bottom = (viewport?.offsetTop || 0) + (viewport?.height || window.innerHeight);
      panel.style.setProperty('--gems-help-height', `${Math.max(0, Math.floor(bottom - top))}px`);
    }
    positionOpenOverlays() {
      if (!this.hasOpenOverlays()) return;
      if (this.mobile) this.syncMobileLockState();
      if (this.drawerOpen) {
        this.positionDrawer();
        this.positionLocaleControls();
        this.scheduleDrawerScroll();
      }
      if (this.cartOpen) this.positionCart();
      this.syncCartConfirmations();
      if (this.mobileSearchOpen) this.positionMobileSearch();
      if (this.searchHelpPanels?.some(panel => !panel.hidden)) this.positionSearchHelp();
    }
    scheduleOverlayPosition() {
      if (!this.hasOpenOverlays()) return;
      if (this.overlayFrame) return;
      this.overlayFrame = requestAnimationFrame(() => {
        this.overlayFrame = 0;
        this.positionOpenOverlays();
      });
    }
    getVisibleHeaderBottom() {
      if (!this.mobile && this.desktopHeaderFixed) return Math.ceil(this.desktopVisualHeight || this.desktopMainHeight || 0);
      const bar = this.mobile ? this.header?.querySelector('.gems-mobile-bar') : this.header;
      if (!bar) return 0;
      const viewportTop = this.mobile ? (window.visualViewport?.offsetTop || 0) : 0;
      return Math.max(viewportTop, Math.ceil(bar.getBoundingClientRect().bottom));
    }
    positionDrawer() {
      if (!this.drawer) return;
      this.drawer.style.setProperty('--gems-overlay-top', `${this.getVisibleHeaderBottom()}px`);
    }
    positionMobileSearch() {
      if (!this.mobileSearch || !this.mobile) return;
      const top = this.getVisibleHeaderBottom();
      const viewport = window.visualViewport;
      const bottom = viewport
        ? (viewport.offsetTop || 0) + viewport.height
        : window.innerHeight;
      this.mobileSearch.style.setProperty('--gems-mobile-search-top', `${top}px`);
      this.mobileSearch.style.setProperty('--gems-mobile-search-height', `${Math.max(0, Math.floor(bottom - top))}px`);
      this.searchBackdrop?.style.setProperty('--gems-mobile-search-top', `${top}px`);
    }
    positionCart() {
      if (!this.cartOpen || !this.cartPopover || !this.activeCartToggle) return;
      const viewport = window.visualViewport;
      const viewportWidth = Math.round(viewport?.width || document.documentElement.clientWidth);
      const viewportHeight = Math.round(viewport?.height || window.innerHeight);
      const viewportLeft = Math.round(viewport?.offsetLeft || 0);
      const viewportTop = Math.round(viewport?.offsetTop || 0);
      const headerBottom = this.getVisibleHeaderBottom();
      if (this.mobile) {
        const continuousScroll = viewportWidth >= 700 || viewportWidth > viewportHeight;
        const top = Math.max(viewportTop, headerBottom);
        const height = Math.max(0, viewportTop + viewportHeight - top);
        this.cartPopover.classList.toggle('is-compact-scroll', continuousScroll);
        this.cartPopover.style.left = `${viewportLeft}px`;
        this.cartPopover.style.right = 'auto';
        this.cartPopover.style.top = `${top}px`;
        this.cartPopover.style.width = `${viewportWidth}px`;
        this.cartPopover.style.height = `${height}px`;
        this.cartPopover.style.maxHeight = `${height}px`;
        return;
      }
      const { left, top, width, maxHeight } = this.getDesktopCartGeometry(this.activeCartToggle);
      const continuousScroll = viewportWidth <= 1180 && (viewportWidth >= 700 || viewportWidth > viewportHeight);
      this.cartPopover.classList.toggle('is-compact-scroll', continuousScroll);
      this.cartPopover.style.left = `${Math.round(left)}px`;
      this.cartPopover.style.right = 'auto';
      this.cartPopover.style.top = `${Math.round(top)}px`;
      this.cartPopover.style.width = `${Math.round(width)}px`;
      // Desktop follows its content, including the empty state. The viewport
      // limits scrolling; it must never supply the dropdown's minimum height.
      this.cartPopover.style.height = 'auto';
      this.cartPopover.style.maxHeight = `${Math.round(maxHeight)}px`;
      this.cartPopover.style.setProperty('--gems-cart-available-height', `${Math.round(maxHeight)}px`);
    }
    getDesktopCartGeometry(trigger) {
      const viewport = window.visualViewport;
      const viewportWidth = Math.round(viewport?.width || document.documentElement.clientWidth);
      const viewportHeight = Math.round(viewport?.height || window.innerHeight);
      const viewportLeft = Math.round(viewport?.offsetLeft || 0);
      const viewportTop = Math.round(viewport?.offsetTop || 0);
      const triggerRect = trigger.getBoundingClientRect();
      const edge = 16;
      const width = Math.min(440, viewportWidth - edge * 2);
      const left = Math.max(viewportLeft + edge, Math.min(triggerRect.right - width, viewportLeft + viewportWidth - width - edge));
      const top = Math.max(viewportTop + edge, this.getVisibleHeaderBottom() + 7);
      const maxHeight = Math.max(0, viewportTop + viewportHeight - top - edge);
      return { left, top, width, maxHeight };
    }
    observeCartConfirmations() {
      this.cartConfirmationObserver?.disconnect();
      this.cartConfirmations = [...document.querySelectorAll('[data-cart-popup-wrapper], [data-cart-popup-frequently-wrapper]')];
      this.cartConfirmationObserver = new MutationObserver(() => this.syncCartConfirmations());
      this.cartConfirmations.forEach((popup) => {
        popup.setAttribute('data-gems-cart-confirmation', '');
        this.cartConfirmationObserver.observe(popup, { attributes: true, attributeFilter: ['class', 'hidden'] });
      });
      this.syncCartConfirmations();
    }
    syncCartConfirmations() {
      const visiblePopups = this.cartConfirmations?.filter(popup =>
        !popup.hidden && !popup.classList.contains('cart-popup-wrapper--hidden'));
      if (!visiblePopups?.length) return;
      const trigger = this.activeCartToggle || this.cartToggles.find((button) => button.offsetParent !== null);
      // Mobile owns only the vertical offset. All popup presentation and
      // lifecycle behavior remain with the existing theme/popup code.
      const geometry = this.mobile ? {
        top: this.getVisibleHeaderBottom()
      } : trigger ? this.getDesktopCartGeometry(trigger) : null;
      visiblePopups.forEach((popup) => {
        const superseded = !this.mobile && this.cartOpen;
        // Keep a dismissed popup masked after rapid dropdown closure so its
        // unfinished legacy exit transition cannot flash back into view.
        if (superseded) popup.setAttribute('data-gems-cart-superseded', '');
        if (geometry) {
          Object.entries(geometry).forEach(([key, value]) => {
            popup.style.setProperty(`--gems-confirmation-${key}`, `${Math.round(value)}px`);
          });
        }
        if (!superseded || popup.hidden || popup.classList.contains('cart-popup-wrapper--hidden')) return;
        // Use the existing close handler: it cancels its timer and releases focus.
        // Do not bubble a synthetic outside click or restore focus to Add to cart.
        const focusedInside = popup.contains(document.activeElement);
        const close = popup.querySelector('[data-cart-popup-close], [data-cart-popup-close-frequently]');
        if (window.theme?.cartPopup?.dismiss) window.theme.cartPopup.dismiss(popup);
        else close?.dispatchEvent(new MouseEvent('click', { detail: 1, bubbles: false }));
        // A pending add can reveal the popup while the dropdown is already open.
        // The scoped observer dismisses it before paint, including missing handlers.
        if (!popup.classList.contains('cart-popup-wrapper--hidden')) {
          popup.classList.add('cart-popup-wrapper--hidden');
          window.theme?.cartPopupAutoClose?.cancel();
          if (window.jQuery && window.slate?.a11y?.removeTrapFocus) {
            window.slate.a11y.removeTrapFocus({ $container: window.jQuery(popup), namespace: 'cartPopupFocus' });
          }
        }
        if (focusedInside) trigger?.focus({ preventScroll: true });
      });
    }
    handleDocumentClick(event) {
      if (!event.target.closest('[data-gems-account]')) this.closeAccount();
      if (!event.target.closest('[data-gems-locale]')) this.closeLocaleControls();
      else if (event.target.closest('[data-gems-locale] .dropdown-item')) queueMicrotask(() => this.closeLocaleControls());
      if (this.cartOpen && this.cartPopover && !this.cartPopover.contains(event.target) && !event.target.closest('[data-gems-cart-toggle]')) {
        this.closeCart();
      }
      if (!event.target.closest('[data-gems-nav-item]')) this.closeAllMega();
      if (!event.target.closest('[data-gems-search-help]') && !event.target.closest('[data-gems-search-help-toggle]')) this.closeSearchHelp();
      if (this.mobileSearchOpen && this.mobileSearch && !this.mobileSearch.contains(event.target) && !event.target.closest('[data-gems-mobile-search-toggle]')) {
        this.closeMobileSearch();
      }
    }
    handleKeydown(event) {
      if (event.defaultPrevented) return;
      if (event.key === 'Escape') {
        if (this.accountMenu && !this.accountMenu.hidden) {
          event.preventDefault();
          this.closeAccount(true);
          return;
        }
        if (this.localeControls?.some(record => !record.menu.hidden)) {
          event.preventDefault();
          this.closeLocaleControls(true);
          return;
        }
        const mega = this.querySelector('[data-gems-nav-item].is-open');
        const help = this.searchHelpToggles.find((button) => button.getAttribute('aria-expanded') === 'true');
        const trigger = this.drawerOpen ? this.lastDrawerTrigger
          : this.cartOpen ? this.activeCartToggle
          : help ? help
          : this.mobileSearchOpen ? this.mobileSearchToggle
          : mega?.querySelector('[data-gems-nav-toggle]');
        if (!trigger) return;
        event.preventDefault();
        if (this.drawerOpen) this.closeDrawer(false);
        else if (this.cartOpen) this.closeCart();
        else if (help) this.closeSearchHelp(null, true);
        else if (this.mobileSearchOpen) this.closeMobileSearch();
        else this.closeAllMega();
        trigger.focus({ preventScroll: true });
        return;
      }
      if (event.key !== 'Tab' || !this.drawerOpen || !this.drawerPanel) return;
      const focusable = [...this.drawerPanel.querySelectorAll(FOCUSABLE)].filter((node) => node.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && (document.activeElement === first || !focusable.includes(document.activeElement))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (document.activeElement === last || !focusable.includes(document.activeElement))) {
        event.preventDefault();
        first.focus();
      }
    }
  }
  customElements.define(ELEMENT_NAME, GemsHeader);
})();

